import { useCallback, useMemo } from "react";
import { useTran } from "@/hooks";

import { Badge } from "@/components/ui/badge";
import UserActions from "../components/UserActions";
import FollowUpCell from "../components/FollowUpCell";
import { useUserListDerived } from "./useUserListDerived";
import { useUserStore } from "./useUserStore";
import { resolveRoleLabel } from "../utils/user-role-options";
import { Button } from "@/components/ui/button";
import ViewOrgs from "@/components/ui/ViewOrgs";
import {
  buildOrgStatusInfoMap,
  buildOrgNameToStatusInfoMap,
} from "@/components/common/organization.utils";

const STATUS_STYLES = {
  active: "border-green-500 bg-green-50 text-green-700",
  locked: "border-red-500 bg-red-50 text-red-700",
  inactive: "border-amber-500 bg-amber-50 text-amber-700",
  deleted: "border-slate-400 bg-slate-100 text-slate-700",
};

const formatStatusLabel = (status = "") =>
  status
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const formatLockUntil = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString();
};

export const useUserListColumns = ({
  onViewUser,
  onDeleteUser,
  onUnlockUser,
} = {}) => {
  const { t } = useTran();
  const { filters, setFilters, resetFilters, setPage } = useUserStore();
  const { isAdminRole, organizationTree } = useUserListDerived();

  const handleUsernameFilterChange = useCallback(
    (value) => {
      setFilters({ username: value });
      setPage(1);
    },
    [setFilters, setPage],
  );

  const handleEmailFilterChange = useCallback(
    (value) => {
      setFilters({ email: value });
      setPage(1);
    },
    [setFilters, setPage],
  );

  const handleRoleFilterChange = useCallback(
    (value) => {
      setFilters({ role: value });
      setPage(1);
    },
    [setFilters, setPage],
  );

  const handleCompanyFilterChange = useCallback(
    (value) => {
      const filterKey = isAdminRole ? "company" : "org";
      setFilters({ [filterKey]: value });
      setPage(1);
    },
    [isAdminRole, setFilters, setPage],
  );

  const handleClearFilters = useCallback(() => {
    resetFilters();
    setPage(1);
  }, [resetFilters, setPage]);

  const organizationColumnLabel = isAdminRole
    ? t("Company")
    : t("Organization");
  const organizationSortKey = isAdminRole ? "company" : "organization";
  const organizationFilterPlaceholder = isAdminRole
    ? t("Filter by company")
    : t("Filter by organization");

  const organizationMap = useMemo(() => {
    const map = new Map();
    const visit = (nodes = []) => {
      nodes.forEach((node) => {
        const value = node?.id || node?._id || node?.key;
        const label = node?.name || node?.title || node?.companyName || value;
        if (value) {
          map.set(String(value), String(label || value));
        }
        if (Array.isArray(node?.children) && node.children.length > 0) {
          visit(node.children);
        }
      });
    };
    visit(organizationTree || []);
    return map;
  }, [organizationTree]);

  const orgStatusMap = useMemo(
    () => buildOrgStatusInfoMap(organizationTree || []),
    [organizationTree],
  );

  const orgNameToStatusMap = useMemo(
    () => buildOrgNameToStatusInfoMap(organizationTree || []),
    [organizationTree],
  );

  const resolveOrganizationList = useCallback(
    (user) => {
      const raw = user?.organizationUnitId;
      const items = Array.isArray(raw) ? raw : raw ? [raw] : [];
      const names = items
        .map((entry) => {
          if (!entry) return null;
          if (typeof entry === "string" || typeof entry === "number") {
            const key = String(entry);
            const found = organizationMap.get(key);
            const info = orgStatusMap.get(key);

            return found
              ? {
                  id: key,
                  name: found,
                  status: info?.status,
                  inactiveDate: info?.inactiveDate,
                }
              : {
                  id: key,
                  name: key,
                  status: info?.status,
                  inactiveDate: info?.inactiveDate,
                };
          }

          // OBJECT ENTRY
          if (typeof entry === "object") {
            const key =
              entry._id ||
              entry.id ||
              entry.key ||
              entry.value ||
              entry.organizationUnitId;
            if (key) {
              const normalized = String(key);
              const found = organizationMap.get(normalized);
              const info = orgStatusMap.get(normalized);

              return found
                ? {
                    id: normalized,
                    name: found,
                    status: info?.status,
                    inactiveDate: info?.inactiveDate,
                  }
                : {
                    id: normalized,
                    name: normalized,
                    status: info?.status,
                    inactiveDate: info?.inactiveDate,
                  };
            }
            const fallback =
              entry.name ||
              entry.label ||
              entry.title ||
              entry.organizationUnitName;

            if (fallback) {
              const fallbackStr = String(fallback);
              const info = orgNameToStatusMap.get(fallbackStr);
              return {
                id: fallbackStr,
                name: fallbackStr,
                status: info?.status,
                inactiveDate: info?.inactiveDate,
              };
            }
            return null;
          }
          return String(entry);
        })
        .filter(Boolean);

      if (user?.assignedToParentCompany) {
        const companyName = user?.company || t("Parent Company");
        const companyId = user?.companyId || "parent-company";
        const parentInfo =
          companyId && companyId !== "parent-company"
            ? orgStatusMap.get(companyId)
            : orgNameToStatusMap.get(companyName);

        names.unshift({
          id: companyId,
          name: companyName,
          status: parentInfo?.status,
          inactiveDate: parentInfo?.inactiveDate,
        });
      }

      return names;
    },
    [organizationMap, orgStatusMap, orgNameToStatusMap, t],
  );

  return useMemo(() => {
    const baseColumns = [
      {
        key: "username",
        header: t("Display Name"),
        sortable: true,
        sortKey: "username",
        render: (user) => (
          <div className="flex flex-col">
            <span className="font-medium text-foreground">
              {user.username || t("Not available")}
            </span>
            {user.onlineStatus === "online" ? (
              <span className="text-xs text-emerald-600">{t("Online")}</span>
            ) : user.lastActiveAgo ? (
              <span className="text-xs text-muted-foreground">
                {t("Last active {{time}}", { time: user.lastActiveAgo })}
              </span>
            ) : (
              <span className="text-xs text-amber-600">{t("Offline")}</span>
            )}
          </div>
        ),
        filter: {
          type: "text",
          value: filters.username,
          onChange: handleUsernameFilterChange,
          placeholder: t("Filter by display name"),
        },
      },
      {
        key: "email",
        header: t("Email"),
        sortable: true,
        sortKey: "email",
        render: (user) => user.email || t("Not available"),
        filter: {
          type: "text",
          value: filters.email,
          onChange: handleEmailFilterChange,
          placeholder: t("Filter by email"),
        },
      },
      {
        key: "role",
        header: t("Role"),
        sortable: true,
        sortKey: "role",
        render: (user) =>
          resolveRoleLabel(user.role, t, undefined, user.customRoleName) ||
          t("Not available"),
        filter: {
          type: "text",
          value: filters.role,
          onChange: handleRoleFilterChange,
          placeholder: t("Filter by role"),
        },
      },
      ...(isAdminRole
        ? [
            {
              key: "status",
              header: t("Status"),
              render: (user) => {
                const status = user?.status || "";
                const lockUntil = formatLockUntil(user?.lockUntil);
                const badgeClassName =
                  STATUS_STYLES[status] ||
                  "border-slate-300 bg-slate-50 text-slate-700";

                return status ? (
                  <div className="flex flex-col gap-1">
                    <Badge
                      variant="outline"
                      className={`w-fit uppercase font-medium ${badgeClassName}`}
                    >
                      {t(formatStatusLabel(status))}
                    </Badge>
                    {status === "locked" && lockUntil ? (
                      <span className="text-xs text-muted-foreground">
                        {t("Until {{time}}", { time: lockUntil })}
                      </span>
                    ) : null}
                  </div>
                ) : (
                  t("Not available")
                );
              },
            },
          ]
        : []),
      {
        key: "organization",
        header: organizationColumnLabel,
        sortable: false,
        sortKey: organizationSortKey,
        render: (user) => {
          if (isAdminRole) {
            return user?.company || t("Not assigned");
          }
          if (user?.role === "super_admin") {
            return (
              <Badge className="bg-[hsl(var(--target-badge))] text-[hsl(var(--target-badge-foreground))] border-transparent">
                {user?.company || t("Not assigned")}
              </Badge>
            );
          }
          const organizations = resolveOrganizationList(user);
          return organizations.length > 0 ? (
            <ViewOrgs
              orgs={organizations}
              tree={organizationTree}
              searchKeyWord={filters.org}
            />
          ) : (
            t("Not assigned")
          );
        },
        filter: {
          type: "text",
          value: isAdminRole ? filters.company : filters.org,
          onChange: handleCompanyFilterChange,
          placeholder: organizationFilterPlaceholder,
        },
      },
      ...(isAdminRole
        ? [
            {
              key: "incharge",
              header: t("Incharge"),
              width: "14%",
              render: (user) => <FollowUpCell user={user} />,
            },
          ]
        : []),
      {
        key: "actions",
        header: t("Actions"),
        align: "right",
        width: "10%",
        filter: {
          render: (
            <Button
              type="button"
              variant="ghost"
              onClick={handleClearFilters}
              className="h-auto px-0 text-sm text-destructive "
            >
              {t("Clear All Filters")}
            </Button>
          ),
        },
        render: (user) => {
          return (
            <div className="flex justify-end">
              <UserActions
                user={user}
                onViewUser={onViewUser}
                onDeleteUser={onDeleteUser}
                onUnlockUser={onUnlockUser}
              />
            </div>
          );
        },
      },
    ];

    return baseColumns;
  }, [
    filters.company,
    filters.email,
    filters.username,
    filters.org,
    filters.role,
    handleClearFilters,
    handleCompanyFilterChange,
    handleEmailFilterChange,
    handleRoleFilterChange,
    handleUsernameFilterChange,
    isAdminRole,
    organizationColumnLabel,
    organizationFilterPlaceholder,
    organizationSortKey,
    organizationTree,
    resolveOrganizationList,
    t,
    onViewUser,
    onDeleteUser,
    onUnlockUser,
  ]);
};
